import type { RelayControlRecoverySummary } from "@cmmd-forge/client-runtime";
import { UnplugIcon } from "lucide-react";

import { DENSE_RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import { SidebarMenu, SidebarMenuItem } from "../ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { getEnvironmentKindIcon, type EnvironmentKind } from "./environmentKind";
import { RelayControlRecoveryRow } from "./RelayControlRecoveryRow";
import {
  RuntimeGroupStatusIndicator,
  type RuntimeGroupStatus,
} from "./RuntimeGroupStatusIndicator";

export type RuntimeDisconnectAction =
  | { readonly state: "unavailable" }
  | {
      readonly state: "available";
      readonly isDisconnecting: boolean;
      readonly onDisconnect: () => void;
    };

export interface ProjectRuntimeControl {
  readonly key: string;
  readonly label: string;
  readonly kind: EnvironmentKind;
  readonly status?: RuntimeGroupStatus | undefined;
  readonly disconnectAction: RuntimeDisconnectAction;
  readonly relayRecovery: {
    readonly summary: RelayControlRecoverySummary;
    readonly onAcknowledge: () => void;
  } | null;
}

function RuntimeControlRow({ control }: { readonly control: ProjectRuntimeControl }) {
  const EnvironmentIcon = getEnvironmentKindIcon(control.kind);
  const disconnectLabel = `Disconnect ${control.label}`;

  return (
    <SidebarMenuItem data-testid={`runtime-control-${control.key}`}>
      <div className="flex min-h-touch min-w-0 items-center gap-2 px-2 text-muted-foreground md:min-h-8">
        <span className="inline-flex size-5 shrink-0 items-center justify-center text-muted-foreground/65">
          <EnvironmentIcon className="size-3" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-foreground/80">{control.label}</span>
        {control.status ? <RuntimeGroupStatusIndicator status={control.status} /> : null}
        {control.disconnectAction.state === "available" ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={disconnectLabel}
                  disabled={control.disconnectAction.isDisconnecting}
                  className={`${DENSE_RESPONSIVE_ICON_BUTTON_CLASS} inline-flex cursor-pointer items-center justify-center text-muted-foreground/60 transition-colors hover:bg-accent/65 hover:text-destructive focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-default disabled:opacity-35`}
                  onClick={control.disconnectAction.onDisconnect}
                />
              }
            >
              <UnplugIcon className="size-3.5" />
            </TooltipTrigger>
            <TooltipPopup side="top">
              {control.disconnectAction.isDisconnecting ? "Disconnecting device" : disconnectLabel}
            </TooltipPopup>
          </Tooltip>
        ) : null}
      </div>
      {control.relayRecovery ? (
        <RelayControlRecoveryRow
          summary={control.relayRecovery.summary}
          onAcknowledge={control.relayRecovery.onAcknowledge}
        />
      ) : null}
    </SidebarMenuItem>
  );
}

export function ProjectRuntimeControls({
  controls,
}: {
  readonly controls: readonly ProjectRuntimeControl[];
}) {
  if (controls.length === 0) return null;
  return (
    <div className="mt-2">
      <div className="px-2 pb-1 text-[10px] font-medium tracking-[0.06em] text-muted-foreground/45 uppercase">
        Devices
      </div>
      <SidebarMenu>
        {controls.map((control) => (
          <RuntimeControlRow key={control.key} control={control} />
        ))}
      </SidebarMenu>
    </div>
  );
}
